// components/VideoCard.jsx

import React, { useCallback } from 'react';

const formatDuration = (seconds) => {
    if (typeof seconds !== 'number' || seconds < 0) return 'N/A';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    const parts = [];
    if (hours > 0) parts.push(String(hours));
    // Pad minutes if hours are present, otherwise single digit is fine (e.g., 8:15 vs 0:08:15)
    parts.push(String(minutes).padStart(hours > 0 ? 2 : 1, '0'));
    // Always pad seconds
    parts.push(String(remainingSeconds).padStart(2, '0'));

    return parts.join(':');
};

const VideoCard = ({ video }) => {

    // --- Data Properties Mapped with Fallbacks ---
    const title = video.title || 'Untitled Video';
    // Use description, then short_text, then a default
    const description = video.description || video.short_text || 'No description available.';
    // Format the duration using the utility function
    const duration = formatDuration(video.duration);
    // Use imageUrl, then thumbnail, then a default image path
    const imageUrl = video.imageUrl || video.thumbnail || '/images/default-videos.webp';
    const channelName = video.channel_name || 'Channel Unknown';
    const date = video.date || 'N/A';

    // --- Theme Utility Classes ---
    // Defined explicitly for clarity in the JSX, similar to DemoCard
    const cardTitleClass = 'text-text-light dark:text-text-dark';
    const cardMutedTextClass = 'text-muted-light dark:text-muted-dark';
    const cardBorderClass = 'border-border-light dark:border-border-dark';

    // --- Click Handler for External Navigation ---
    const safeHandleClick = useCallback(() => {
        if (video.url) {
            // Open the video URL in a new tab/window
            window.open(video.url, '_blank', 'noopener,noreferrer');
        }
    }, [video.url]);

    return (

        <div
            className="w-full h-full"
        >
            <div 
                className="card h-full cursor-pointer rounded-xl overflow-hidden bg-card-light dark:bg-card-dark duration-300 ease-in-out"
            >
                <div className="card-img-container h-[200px] overflow-hidden relative"> {/* Using 200px height */}
                    <img
                        src={imageUrl}
                        alt={`Thumbnail for ${title}`}
                        // Image scale effect on card hover
                        className="card-img w-full h-full object-cover transition-transform duration-500 ease-in-out"
                        loading="lazy"
                        onError={e => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/images/default-videos.webp';
                        }}
                        // Mimic the image hover effect from ArticleCard/DemoCard for consistency
                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1.0)'}
                    />

                    {/* Duration Overlay */}
                    <span className="absolute bottom-2 right-2 px-2 py-0.5 text-xs font-semibold bg-black bg-opacity-70 text-white rounded">
                        {duration}
                    </span>
                </div>

                {/* Card Body: Uses flex-1 and min-h for consistent layout */}
                <div className="card-body p-4 flex flex-col flex-1"> {/* p-4 like ArticleCard */}

                    {/* Title */}
                    <h5 className={`card-title text-lg font-semibold mb-2 ${cardTitleClass} line-clamp-2`}>
                        {title}
                    </h5>

                    {/* Description: Fixed space area for uniform card height */}
                    {/* Using min-h-[3rem] for consistency with ArticleCard's text area */}
                    <p className={`card-text text-sm mb-3 ${cardMutedTextClass} line-clamp-3 min-h-[3rem] flex-1`}>
                        {description}
                    </p>

                    {/* Footer: Uses mt-auto and flex to manage channel/date layout */}
                    <div className={`flex flex-col mt-auto pt-2 border-t ${cardBorderClass}`}>

                        {/* Channel Name - Placed above date */}
                        <small className={`text-xs font-medium mb-1 ${cardMutedTextClass}`}>
                            {channelName}
                        </small>

                        <div className="flex justify-between items-center">
                            {/* Date */}
                            <small className={`text-xs ${cardMutedTextClass}`}>
                                {date}
                            </small>

                            {/* View Button */}
                            <button
                                onClick={e => {
                                    e.stopPropagation(); // Prevent card's main onClick from firing twice
                                    safeHandleClick();
                                }}
                                className="text-sm font-medium text-primary dark:text-secondary hover:underline transition-colors flex items-center"
                            >
                                Watch Video &rarr;
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(VideoCard);